import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Sheet,
  Typography,
  FormControl,
  FormLabel,
  CircularProgress
} from '@mui/joy';
import { KeyboardArrowDown, Search } from '@mui/icons-material';

interface BrandSelectorProps {
  brands: string[];
  selectedBrands: string[];
  tempSelectedBrands: string[];
  onBrandToggle: (brand: string) => void;
  onSearch: () => void;
  onModalClose: () => void;
  onClearSelection: () => void;
  onClearAndCommit: () => void;
  onResetTempSelections: () => void;
  disabled?: boolean;
  loading?: boolean;
}

// Memoized brand button component to prevent unnecessary re-renders
const BrandButton = React.memo<{
  brand: string;
  isSelected: boolean;
  onClick: (brand: string) => void;
}>(({ brand, isSelected, onClick }) => {
  const handleClick = useCallback(() => {
    onClick(brand);
  }, [brand, onClick]);

  return (
    <Button
      variant={isSelected ? 'solid' : 'outlined'}
      color={isSelected ? 'primary' : 'neutral'}
      size="sm"
      onClick={handleClick}
      sx={{
        justifyContent: 'flex-start',
        textAlign: 'left',
        height: 'auto',
        py: 1.5,
        px: 2,
        fontSize: '0.875rem',
        borderRadius: 2,
        borderColor: isSelected ? 'primary.500' : 'neutral.300',
        backgroundColor: isSelected ? 'primary.500' : 'background.surface',
        whiteSpace: 'nowrap',
        '&:hover': {
          backgroundColor: isSelected ? 'primary.600' : 'neutral.50',
          borderColor: isSelected ? 'primary.600' : 'neutral.400',
        }
      }}
    >
      <Typography
        level="body-sm"
        sx={{
          fontWeight: isSelected ? 'bold' : 'normal',
          color: isSelected ? 'white' : 'neutral.700',
        }}
      >
        {brand}
      </Typography>
    </Button>
  );
});

BrandButton.displayName = 'BrandButton';

export const BrandSelector: React.FC<BrandSelectorProps> = React.memo(({
  brands,
  selectedBrands,
  tempSelectedBrands,
  onBrandToggle,
  onSearch,
  onModalClose,
  onClearSelection,
  onClearAndCommit,
  onResetTempSelections,
  disabled = false,
  loading = false
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Memoize the button text to prevent recalculation on every render
  const buttonText = useMemo(() => {
    if (selectedBrands.length === 0) {
      return 'All Brands';
    } else if (selectedBrands.length === 1) {
      return selectedBrands[0];
    } else {
      return `${selectedBrands.length} brands selected`;
    }
  }, [selectedBrands]);

  // Memoize the button styles to prevent recalculation
  const buttonStyles = useMemo(() => ({
    minWidth: selectedBrands.length > 0 ? 'auto' : 200,
    maxWidth: '100%',
    justifyContent: 'space-between',
    backgroundColor: selectedBrands.length > 0 ? 'primary.50' : 'background.surface',
    borderColor: selectedBrands.length > 0 ? 'primary.300' : 'neutral.300',
    '&:hover': {
      backgroundColor: selectedBrands.length > 0 ? 'primary.100' : 'background.surface',
      borderColor: selectedBrands.length > 0 ? 'primary.400' : 'neutral.400',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  }), [selectedBrands.length]);

  // Memoize the brand buttons to prevent recreation on every render
  const brandButtons = useMemo(() => {
    return brands.map((brand) => {
      const isSelected = tempSelectedBrands.includes(brand);
      return (
        <BrandButton
          key={brand}
          brand={brand}
          isSelected={isSelected}
          onClick={onBrandToggle}
        />
      );
    });
  }, [brands, tempSelectedBrands, onBrandToggle]);

  // Memoize the selected brand chips
  const selectedBrandChips = useMemo(() => {
    return selectedBrands.map((brand) => (
      <Chip
        key={brand}
        size="lg"
        color="primary"
        variant="solid"
        sx={{ 
          cursor: 'pointer',
          maxWidth: '150px',
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        }}
      >
        {brand}
      </Chip>
    ));
  }, [selectedBrands]);

  const handleClick = useCallback(() => {
    if (disabled) return;
    if (!open) {
      // Reset temp selections to current selections when opening
      onResetTempSelections();
    }
    setOpen(!open);
  }, [disabled, open, onResetTempSelections]);

  const handleSearch = useCallback(() => {
    onSearch();
    setOpen(false);
  }, [onSearch]);

  const handleClearClick = useCallback(() => {
    onClearAndCommit();
    // Don't close the modal - let user continue selecting brands
  }, [onClearAndCommit]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClose = () => {
      onModalClose();
      setOpen(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (open && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onModalClose]);

  return (
    <FormControl>
      <FormLabel>Filter by:</FormLabel>
      <Box ref={containerRef} sx={{ position: 'relative' }}>
        <Button
          onClick={handleClick}
          variant="outlined"
          size="lg"
          disabled={disabled}
          endDecorator={<KeyboardArrowDown />}
          sx={buttonStyles}
        >
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1, 
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden'
          }}>
            {selectedBrands.length === 0 ? (
              <Typography level="body-md" sx={{ textAlign: 'left' }}>
                {buttonText}
              </Typography>
            ) : (
              selectedBrandChips
            )}
          </Box>
        </Button>

        {open && (
          <Sheet
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              mt: 1,
              width: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'neutral.200',
              backgroundColor: 'background.surface',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box sx={{ p: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                {tempSelectedBrands.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography level="body-sm" color="neutral">
                        Selected ({tempSelectedBrands.length})
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button size="sm" variant="plain" color="danger" onClick={handleClearClick}>
                          Clear All
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {brandButtons}
              </Box>

              {/* Search Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="solid"
                  size="lg"
                  onClick={handleSearch}
                  disabled={loading}
                  startDecorator={loading ? <CircularProgress size="sm" /> : <Search />}
                  sx={{ minWidth: 100 }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </Box>
            </Box>
          </Sheet>
        )}
      </Box>
    </FormControl>
  );
});

BrandSelector.displayName = 'BrandSelector';
